import { motion } from "framer-motion";
import { BedDouble, HandPlatter, HeartHandshake, Sparkles, LayoutGrid, Wrench } from "lucide-react";
import { useLanguage } from "../i18n";

const EASE = [0.22, 1, 0.36, 1];

const COPY = {
  it: {
    kicker: "Il metodo di ospitalità OGStay",
    title: "Standard da alta hôtellerie, dentro una casa.",
    sub: "Sei pilastri che applichiamo a ogni soggiorno: la differenza tra una casa vacanza qualsiasi e una casa OGStay.",
    pillars: [
      { icon: BedDouble, t: "Sleep", d: "Comfort e qualità del riposo: materassi, biancheria e silenzio curati." },
      { icon: Sparkles, t: "Clean", d: "Pulizia professionale a standard alberghiero, a ogni cambio ospite." },
      { icon: HandPlatter, t: "Welcome", d: "Preparazione e accoglienza pensate prima del tuo arrivo." },
      { icon: LayoutGrid, t: "Design", d: "Ambienti curati, funzionali e fotografati come meritano." },
      { icon: Wrench, t: "Care", d: "Controlli e manutenzione costanti, piccoli problemi risolti in fretta." },
      { icon: HeartHandshake, t: "Guest", d: "Assistenza e guest experience: un team locale sempre raggiungibile." },
    ],
  },
  en: {
    kicker: "The OGStay hospitality method",
    title: "High-end hôtellerie standards, inside a home.",
    sub: "Six pillars we apply to every stay: the difference between an ordinary holiday home and an OGStay home.",
    pillars: [
      { icon: BedDouble, t: "Sleep", d: "Rest comfort and quality: mattresses, linen and quiet, curated." },
      { icon: Sparkles, t: "Clean", d: "Professional hotel-grade cleaning at every guest change." },
      { icon: HandPlatter, t: "Welcome", d: "Preparation and a warm welcome designed before you arrive." },
      { icon: LayoutGrid, t: "Design", d: "Curated, functional spaces, photographed as they deserve." },
      { icon: Wrench, t: "Care", d: "Constant checks and maintenance: small issues solved fast." },
      { icon: HeartHandshake, t: "Guest", d: "Assistance and guest experience: a local team always reachable." },
    ],
  },
};

export default function HomeStandards() {
  const { lang } = useLanguage();
  const c = COPY[lang];
  return (
    <section id="metodo-home" data-testid="home-standards" className="bg-navy py-24 text-white sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 34 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="max-w-2xl"
        >
          <p className="kicker text-sky">
            <span className="h-px w-10 bg-sky" />
            {c.kicker}
          </p>
          <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">{c.title}</h2>
          <p className="mt-5 text-base leading-relaxed text-white/65 sm:text-lg">{c.sub}</p>
        </motion.div>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {c.pillars.map((p, i) => (
            <motion.div
              key={p.t}
              data-testid={`standard-${p.t.toLowerCase()}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.07, ease: EASE }}
              className="group rounded-[1.75rem] bg-med/40 p-8 ring-1 ring-white/10 transition-colors duration-500 hover:bg-med/60"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sky/15 text-sky transition-transform duration-500 group-hover:scale-110">
                <p.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-6 text-xs font-extrabold uppercase tracking-[0.3em] text-sky">{p.t}</h3>
              <p className="mt-2.5 text-base font-semibold leading-relaxed text-white/85">{p.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
